import { createSelector } from '@reduxjs/toolkit';
import pipe from 'lodash/flow';
import { PaymentMethod } from '../../api';
import { RootState } from '../store';
import { Currency, Provider } from '../../../types/PaymentTypes';

const getPayment = (state: RootState) => state.payment;

export const getAmountOptions = pipe(getPayment, (payment) => payment.amountOptions);

export const getAmountOptionsStatus = pipe(getPayment, (payment) => payment.amountOptionsStatus);

export const getCurrencies = pipe(getPayment, (payment) => payment.currencies);

const getProviders = pipe(getPayment, (payment) => payment.providers);

export const getDefaultCurrencyCode = pipe(getPayment, (payment) => payment.defaultCurrencyCode);

export const getActiveDonation = pipe(getPayment, (payment) => payment.donation);

export const getPreviousSubscription = pipe(getPayment, (payment) => payment.previousSubscription);

export const getActiveCurrencyCode = pipe(
  getPayment,
  (payment) =>
    payment.activeCurrencyCode ||
    payment.previousSubscription?.currencyCode ||
    payment.defaultCurrencyCode,
);

export const getActiveAmountOptions = createSelector(
  getAmountOptions,
  getActiveCurrencyCode,
  (amountOptions, activeCurrencyCode) => {
    if (!activeCurrencyCode) {
      return;
    }
    return amountOptions[activeCurrencyCode];
  },
);

export const getActiveAmountOptionsStatus = createSelector(
  getAmountOptionsStatus,
  getActiveCurrencyCode,
  (amountOptionsStatus, activeCurrencyCode) => {
    if (!activeCurrencyCode) {
      return;
    }
    return amountOptionsStatus[activeCurrencyCode];
  },
);

const variantPropsSelector = (_: unknown, { variant }: { variant: Provider }) => variant;

export const makeCurrenciesForVariantSelector = () =>
  createSelector(
    getCurrencies,
    getProviders,
    variantPropsSelector,
    (currencies, providers, variant) => {
      return {
        currencies: (providers[variant]?.currencyCodes || []).map(
          (code) => currencies[code] as Currency,
        ),
      };
    },
  );

export const getStripeCurrencies = createSelector(
  getCurrencies,
  getProviders,
  (currencies, providers) => {
    return (providers.stripe?.currencyCodes || []).map((code) => currencies[code] as Currency);
  },
);

export const getActiveProviderData = createSelector(
  getActiveDonation,
  getProviders,
  (donation, providers) => {
    if (!donation?.provider) {
      return;
    }
    return providers[donation.provider];
  },
);

export const getActiveProviderMethodsReady = createSelector(
  getActiveProviderData,
  (providerData) => {
    return providerData?.paymentMethodsStatus?.ready;
  },
);

export const getActiveDonationPaymentMethod = createSelector(
  getActiveProviderData,
  getActiveDonation,
  (providerData, donation) => {
    const paymentMethods = providerData?.paymentMethods;
    const findPaymentMethod: (paymentMethod: PaymentMethod) => boolean = donation?.paymentMethodId
      ? (paymentMethod) => paymentMethod.id === donation?.paymentMethodId
      : (paymentMethod) => !!paymentMethod.default;
    return paymentMethods?.find(findPaymentMethod);
  },
);

export const getActiveSubscription = pipe(getPayment, (payment) => payment.subscription);

export const getActiveSubscriptionDefaultDetails = createSelector(
  getActiveSubscription,
  (subscription) => {
    return subscription?.details?.items.find(({ type }) => type === 'DEFAULT');
  },
);

export const getAffiliate = pipe(getPayment, (payment) => payment.affiliate);

export const getPaymentFlowId = pipe(getPayment, (payment) => payment?.paymentFlowId);
